// Looks panel — retextured variants of one avatar.
//
// A look changes what the avatar is wearing without changing who it is. The
// mesh, rig and animations are untouched; only the base-colour map differs.
//
// Meshy takes a couple of minutes, so a new look is created as a pending row
// and polled in the background. The panel can be closed and reopened without
// losing it — the task id lives in the database, not in component state.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Loader, RefreshCw, Shirt, Sparkles, Trash2, TriangleAlert, X } from 'lucide-react';
import {
  AvatarLook,
  applyLook,
  createLook,
  deleteLook,
  fetchLooks,
  refreshLook,
} from '../services/avatarLookService';
import { supabase } from '../config/supabase';
import { captureModelThumbnail } from '../utils/captureModelThumbnail';
import { uploadBytesToStorage } from '../utils/uploadToStorage';
import './AvatarLooks.css';


/**
 * Marks look pictures we rendered ourselves. v2: rendered with the emissive
 * layer swapped too — v1 pictures still showed the original outfit glowing
 * through (avatars whose texture doubles as the emissive map), so they are
 * re-rendered on sight.
 */
export const LOOK_THUMB_MARK = '_selfthumb2';

export const AvatarLooks: React.FC<{
  avatarId: string;
  modelUrl: string;
  avatarName?: string;
  /** Picture of the avatar in its original texture, for the Original tile. */
  originalThumbnailUrl?: string | null;
  activeLookId: string | null;
  onApplied: (lookId: string | null, textureUrl: string | null, thumbnailUrl?: string | null) => void;
  /** A look's picture was (re)rendered — lets the wardrobe card update. */
  onLookThumbnail?: (lookId: string, thumbnailUrl: string) => void;
  onClose: () => void;
  /** Open the Outfit Studio (new garments, new geometry) for this avatar. */
  onNewOutfit?: () => void;
}> = ({ avatarId, modelUrl, avatarName, originalThumbnailUrl, activeLookId, onApplied, onLookThumbnail, onClose, onNewOutfit }) => {
  const [looks, setLooks] = useState<AvatarLook[]>([]);
  const [prompt, setPrompt] = useState('');
  const [hair, setHair] = useState('');
  const [makeup, setMakeup] = useState('');
  const [busy, setBusy] = useState(false);
  const [originalThumbFailed, setOriginalThumbFailed] = useState(false);
  const [error, setError] = useState('');
  const timer = useRef<number | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // The wardrobe lives inside pages that attach their own keyboard shortcuts.
  // Without a hard stop at the modal boundary those handlers see every
  // keystroke first, which is what made the prompt box feel like it refused
  // input. Capture-phase, so nothing upstream gets a look-in.
  useEffect(() => {
    const el = modalRef.current;
    if (!el) return;
    const stop = (e: Event) => e.stopPropagation();
    const evts = ['keydown', 'keyup', 'keypress', 'pointerdown', 'wheel'];
    evts.forEach((t) => el.addEventListener(t, stop, { capture: true }));
    return () => evts.forEach((t) => el.removeEventListener(t, stop, { capture: true }));
  }, []);
  // Looks whose thumbnail has already been (re)rendered this session, so the
  // backfill below cannot loop on rows it has just fixed.
  const thumbed = useRef<Set<string>>(new Set());

  /**
   * Render our own tile image: this avatar's model wearing the look's actual
   * texture. Meshy's task thumbnail pictures ITS re-export, which repeatedly
   * showed a different outfit than the one the texture applies — the tile
   * contradicted the avatar and made the feature look broken. '_selfthumb' in
   * the stored URL marks rows already migrated to a truthful image.
   */
  const regenThumb = useCallback(async (look: AvatarLook) => {
    if (!look.texture_url || thumbed.current.has(look.id)) return;
    thumbed.current.add(look.id);
    try {
      const blob = await captureModelThumbnail(modelUrl, 384, look.texture_url);
      if (!blob) return;
      const url = await uploadBytesToStorage(blob, `avatars/looks/${look.id}${LOOK_THUMB_MARK}.png`, 'image/png');
      const stamped = `${url}?v=${Date.now()}`;
      await supabase.from('avatar_looks').update({ thumbnail_url: stamped }).eq('id', look.id);
      setLooks((prev) => prev.map((l) => (l.id === look.id ? { ...l, thumbnail_url: stamped } : l)));
      onLookThumbnail?.(look.id, stamped);
    } catch (err) {
      console.error('[looks] thumbnail render failed', look.id, err);
    }
  }, [modelUrl, onLookThumbnail]);

  // Backfill: any ready look still showing a Meshy-derived thumbnail gets a
  // truthful one. Heals existing rows without touching the generation flow.
  useEffect(() => {
    looks
      .filter((l) => l.texture_url && !(l.thumbnail_url || '').includes(LOOK_THUMB_MARK))
      .forEach((l) => { void regenThumb(l); });
  }, [looks, regenThumb]);

  const load = useCallback(async () => {
    try {
      setLooks(await fetchLooks(avatarId));
    } catch (e: any) {
      setError(e?.message || 'Could not load looks.');
    }
  }, [avatarId]);

  useEffect(() => { load(); }, [load]);

  // Poll only while something is actually pending, and stop as soon as nothing
  // is — a timer that runs forever on a settled list is just wasted requests.
  //
  // Failures are surfaced, not swallowed. The first version caught every error
  // and returned the unchanged row, so a failing poll or a rejected database
  // write left the look at 'pending' forever with its button disabled and
  // nothing on screen explaining why. Silence is the worst possible behaviour
  // here: the state that needs debugging is exactly the state that hid itself.
  useEffect(() => {
    const pending = looks.filter((l) => l.status === 'pending');
    if (!pending.length) {
      if (timer.current) { window.clearTimeout(timer.current); timer.current = null; }
      return;
    }
    timer.current = window.setTimeout(async () => {
      const results = await Promise.all(
        pending.map((l) =>
          refreshLook(l).then(
            (u) => ({ ok: true as const, look: u }),
            (e) => ({ ok: false as const, look: l, err: e }),
          ),
        ),
      );
      const failed = results.find((r) => !r.ok) as { err?: any } | undefined;
      if (failed?.err) {
        setError(`Checking status failed: ${failed.err?.message || failed.err}`);
      }
      setLooks((prev) => prev.map((l) => results.find((r) => r.look.id === l.id)?.look || l));
    }, 6000);
    return () => { if (timer.current) window.clearTimeout(timer.current); };
  }, [looks]);

  /** Manual poll. Polling only runs while this panel is mounted, so a look
   *  generated and left alone needs a way to be nudged without regenerating. */
  const checkNow = async (look: AvatarLook) => {
    setError('');
    try {
      const updated = await refreshLook(look);
      setLooks((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
      if (updated.status === 'pending') {
        setError('Still generating — Meshy has not finished yet.');
      } else if (updated.texture_url) {
        // It is ready and the user just asked about it, so put it on rather
        // than making them find and click it again.
        wear(updated);
      }
    } catch (e: any) {
      setError(e?.message || 'Could not check that look.');
    }
  };

  const generate = async () => {
    const p = prompt.trim();
    // Any of the three fields alone is a valid look — hair-only and
    // makeup-only changes are exactly what was asked for.
    if (!p && !hair.trim() && !makeup.trim()) {
      return setError('Describe an outfit, a hair colour, or makeup — any of them.');
    }
    setBusy(true);
    setError('');
    try {
      const look = await createLook({ avatarId, modelUrl, prompt: p, hair, makeup });
      setLooks((prev) => [look, ...prev]);
      setPrompt(''); setHair(''); setMakeup('');
    } catch (e: any) {
      setError(e?.message || 'Could not start the retexture.');
    } finally {
      setBusy(false);
    }
  };

  // Applying is optimistic. The texture is put on the avatar first, then
  // persisted — so a look you can see is a look you can see, even if the
  // database write is the thing that is broken. Saving silently and rendering
  // nothing was the previous behaviour, and it made a visible feature feel
  // dead for reasons the user could not possibly infer.
  const wear = async (look: AvatarLook | null) => {
    setError('');
    // Only our own render shows the real outfit (see regenThumb).
    const thumb = look?.thumbnail_url?.includes(LOOK_THUMB_MARK) ? look.thumbnail_url : null;
    onApplied(look?.id ?? null, look?.texture_url ?? null, thumb);
    try {
      await applyLook(avatarId, look?.id ?? null);
    } catch (e: any) {
      setError(
        `Showing it here, but it did not save: ${e?.message || e}. ` +
        'It will revert on reload.',
      );
    }
  };

  const remove = async (look: AvatarLook) => {
    try {
      if (look.id === activeLookId) await wear(null);
      await deleteLook(look.id);
      setLooks((prev) => prev.filter((l) => l.id !== look.id));
    } catch (e: any) {
      setError(e?.message || 'Could not delete that look.');
    }
  };

  return createPortal(
    <div className="al-scrim" onClick={onClose}>
      <div className="al-modal" ref={modalRef} onClick={(e) => e.stopPropagation()}>
        <header className="al-head">
          <Shirt size={16} />
          <div>
            <h3>Recolour{avatarName ? ` — ${avatarName}` : ''}</h3>
            <span>
              Same clothes, new colours and fabrics. A skirt stays a skirt.
              {onNewOutfit && <> Want different clothes? <button className="al-link" onClick={onNewOutfit}>Make a new outfit</button></>}
            </span>
          </div>
          <button className="al-x" onClick={onClose} aria-label="Close"><X size={17} /></button>
        </header>

        <div className="al-compose">
          <input
            autoFocus
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !busy) generate(); }}
            placeholder="Colours and fabrics — e.g. black leather with silver details"
            maxLength={600}
          />
          <button className="al-go" onClick={generate} disabled={busy}>
            {busy ? <Loader size={14} className="spin" /> : <Sparkles size={14} />}
            Generate
          </button>
        </div>

        <div className="al-row">
          <label className="al-field">
            <span>Hair colour <em>optional</em></span>
            <input
              value={hair}
              onChange={(e) => setHair(e.target.value)}
              placeholder="platinum blonde, jet black, pastel pink…"
              maxLength={60}
            />
          </label>
          <label className="al-field">
            <span>Makeup <em>optional</em></span>
            <input
              value={makeup}
              onChange={(e) => setMakeup(e.target.value)}
              placeholder="red lips, smoky eyes, gold shimmer…"
              maxLength={80}
            />
          </label>
        </div>

        {error && <p className="al-error">{error}</p>}

        <div className="al-grid">
          {/* The original is always reachable. Without it a look would be a
              one-way door, since the source texture is never overwritten. */}
          <button
            className={`al-item ${!activeLookId ? 'on' : ''}`}
            onClick={() => wear(null)}
          >
            <span className="al-thumb al-thumb-orig">
              {originalThumbnailUrl && !originalThumbFailed
                ? <img src={originalThumbnailUrl} alt="" loading="lazy" onError={() => setOriginalThumbFailed(true)} />
                : 'Original'}
            </span>
            <span className="al-name">Original</span>
            {!activeLookId && <span className="al-badge"><Check size={10} /> Worn</span>}
          </button>

          {looks.map((l) => (
            <div key={l.id} className={`al-item ${l.id === activeLookId ? 'on' : ''}`}>
              <button
                className="al-hit"
                onClick={() => l.texture_url && wear(l)}
                disabled={!l.texture_url}
                title={l.texture_url ? 'Wear this look' : ''}
              >
                <span className="al-thumb">
                  {l.thumbnail_url
                    ? <img src={l.thumbnail_url} alt="" loading="lazy" />
                    : l.status === 'pending'
                      ? <Loader size={16} className="spin" />
                      : l.status === 'failed'
                        ? <TriangleAlert size={16} />
                        : <Shirt size={16} />}
                </span>
                <span className="al-name">{l.name}</span>
                {l.status === 'pending' && <span className="al-sub">Generating…</span>}
                {l.status === 'failed' && (
                  <span className="al-sub al-sub-bad">{l.failure_reason || 'Failed'}</span>
                )}
                {l.id === activeLookId && <span className="al-badge"><Check size={10} /> Worn</span>}
              </button>
              {!l.texture_url && (
                <button className="al-del al-check" onClick={() => checkNow(l)} title="Check again">
                  <RefreshCw size={12} />
                </button>
              )}
              <button className="al-del" onClick={() => remove(l)} title="Delete look">
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default AvatarLooks;
